import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Badge } from 'src/components/ui/badge'
import { Button } from 'src/components/ui/button'
import { useAnnouncementNotifications } from 'src/hooks/useAnnouncementNotifications'
import { useAppSelector } from 'src/store'
import { THEME_MODE } from 'src/interfaces/theme'
import dayjs from 'dayjs'

function AnnouncementNotificationCard() {
    const history = useHistory()
    const location = useLocation()
    const userTheme = useAppSelector((state: any) => state.theme)
    const isDarkMode = userTheme === THEME_MODE.DARK

    const {
        unseenAnnouncements,
        isVisible,
        isLoading,
        handleAnnouncementClick,
        handleClose
    } = useAnnouncementNotifications()

    // Hide notification card on announcement pages
    const isOnAnnouncementPage = location.pathname.startsWith('/announcements')

    // Don't show notification if loading, not visible, or on announcement pages
    if (!isVisible || isLoading || isOnAnnouncementPage) {
        return null
    }

    const formatDate = (dateString: string) => {
        return dayjs(dateString).format('M/D/YYYY')
    }

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'listings':
                return 'bg-blue-600'
            case 'delistings':
                return 'bg-red-600'
            case 'updates':
                return 'bg-green-600'
            default:
                return 'bg-gray-600'
        }
    }

    return (
        <div className="fixed bottom-16 right-4 md:bottom-6 md:right-6 z-[9999] w-72 sm:w-80 md:w-96 lg:w-[420px] max-w-[calc(100vw-2rem)] md:max-w-[calc(100vw-3rem)]">
            <div
                className={`rounded-lg border shadow-lg p-3 md:p-4 ${isDarkMode
                    ? 'bg-[#030711] border-[#424850] text-white'
                    : 'bg-white border-gray-200 text-gray-900'
                    }`}
            >
                {/* Header */}
                <div className="flex justify-between items-center mb-2 md:mb-3">
                    <h3 className={`font-semibold text-xs md:text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Announcements
                    </h3>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClose}
                        className={`h-6 w-6 md:h-8 md:w-8 p-0 rounded-full flex items-center justify-center ${isDarkMode
                            ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                            : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                            }`}
                        title="Close"
                    >
                        <span className="text-sm md:text-lg font-bold leading-none">×</span>
                    </Button>
                </div>

                {/* Announcements List */}
                <div className="space-y-2 md:space-y-3">
                    {unseenAnnouncements.map((announcement) => (
                        <div
                            key={announcement.id}
                            onClick={() => handleAnnouncementClick(announcement)}
                            className={`p-2 md:p-3 rounded-md border cursor-pointer transition-colors ${isDarkMode
                                ? 'border-[#424850] hover:bg-gray-800 bg-[#1a1d1f]'
                                : 'border-gray-200 hover:bg-gray-50 bg-gray-50'
                                }`}
                        >
                            <div className="flex justify-between items-start mb-1 md:mb-2">
                                <div className="flex-1 mr-2">
                                    <h4 className={`text-xs md:text-sm font-medium leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'
                                        }`}>
                                        {announcement.title}
                                    </h4>
                                </div>
                                <Badge className={`text-white text-xs flex-shrink-0 ${getCategoryColor(announcement.category)}`}>
                                    {announcement.category.charAt(0).toUpperCase() + announcement.category.slice(1)}
                                </Badge>
                            </div>

                            {announcement.sub_title && (
                                <p className={`text-xs mb-1 md:mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'
                                    }`}>
                                    {announcement.sub_title}
                                </p>
                            )}

                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                {formatDate(announcement.published_at || announcement.created_at)}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                {unseenAnnouncements.length > 0 && (
                    <div className="mt-2 md:mt-3 pt-2 md:pt-3 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => history.push('/announcements')}
                            className={`w-full text-xs py-1 md:py-2 ${isDarkMode
                                ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-800'
                                : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                                }`}
                        >
                            View all announcements
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AnnouncementNotificationCard 